import { memo, useCallback, useMemo, useState, type FormEvent } from 'react';

export interface IClaimDomainInput {
  domain: string;
  targetIp: string;
  proxied: boolean;
}

interface IDomainClaimFormProps {
  baseDomain: string;
  maxPerUser: number;
  isBusy: boolean;
  remainingClaims: number;
  onClaim: (input: IClaimDomainInput) => Promise<boolean>;
}

const DomainClaimFormComponent = ({
  baseDomain,
  maxPerUser,
  isBusy,
  remainingClaims,
  onClaim
}: IDomainClaimFormProps) => {
  const [domainInput, setDomainInput] = useState('');
  const [targetIpInput, setTargetIpInput] = useState('');
  const [proxied, setProxied] = useState(true);

  const isClaimBlocked = isBusy || remainingClaims <= 0;

  const helperText = useMemo(() => {
    if (remainingClaims <= 0)
      return `You reached your ${maxPerUser}-domain limit. Delete one to claim another.`;
    return `Use a single label (example: myserver). It becomes myserver.${baseDomain}.`;
  }, [baseDomain, maxPerUser, remainingClaims]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (isClaimBlocked) return;

      const didClaimSucceed = await onClaim({
        domain: domainInput,
        targetIp: targetIpInput,
        proxied
      });
      if (!didClaimSucceed) return;

      setDomainInput('');
      setTargetIpInput('');
      setProxied(true);
    },
    [domainInput, isClaimBlocked, onClaim, proxied, targetIpInput]
  );

  return (
    <section className="surface-panel">
      <h2 className="text-lg font-semibold text-foreground">
        Claim a new domain
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{helperText}</p>

      <form className="mt-4 grid gap-3 sm:grid-cols-3" onSubmit={handleSubmit}>
        <div className="flex items-center">
          <input
            className="form-input h-11 flex-1"
            disabled={isClaimBlocked}
            onChange={(event) => setDomainInput(event.target.value)}
            placeholder="myserver"
            value={domainInput}
          />
          <span className="ml-1 text-sm text-muted-foreground">
            .{baseDomain}
          </span>
        </div>
        <input
          className="form-input h-11"
          disabled={isClaimBlocked}
          onChange={(event) => setTargetIpInput(event.target.value)}
          placeholder="203.0.113.10"
          value={targetIpInput}
        />

        <label className="flex items-center gap-2 rounded-xl border border-border bg-background/60 px-3 text-sm text-muted-foreground">
          <input
            checked={proxied}
            className="h-4 w-4"
            disabled={isClaimBlocked}
            onChange={(event) => setProxied(event.target.checked)}
            type="checkbox"
          />
          Enable Cloudflare proxy
        </label>

        <button
          className="primary-button h-11 sm:col-span-3"
          disabled={
            isClaimBlocked ||
            domainInput.trim().length === 0 ||
            targetIpInput.trim().length === 0
          }
          type="submit"
        >
          {isBusy ? 'Claiming...' : 'Claim domain'}
        </button>
      </form>
    </section>
  );
};

export const DomainClaimForm = memo(DomainClaimFormComponent);
